import React from 'react';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
} from '@atlassian/jira-common-constants';
import { ROUTE_GROUPS, ROUTE_NAMES } from '@atlassian/jira-common-constants/src/spa-routes';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import {
	NavigateBackProvider,
	useNavigateBack,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-back-button';
import AsyncBusinessCompanyManagedProjectSettingsSidebar from '@atlassian/jira-navigation-apps-sidebar-nav4-business-company-managed-project-settings';
import AsyncBusinessTeamManagedProjectSettingsSidebar from '@atlassian/jira-navigation-apps-sidebar-nav4-business-team-managed-project-settings';
import { SidebarContainer } from '@atlassian/jira-navigation-apps-sidebar-nav4-common/src/ui/sidebar-container';
import {
	AsyncGlobalAppsSettingsSidebar,
	APPS_SETTINGS_ROUTE_NAMES,
	useGlobalAppsSettingsConnectItemActive,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-apps-settings';
import {
	AsyncGlobalIssuesSettingsSidebar,
	ISSUES_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-issues-settings';
import {
	AsyncGlobalProductsSettingsSidebar,
	PRODUCTS_SETTINGS_OPERATIONS_ROUTE_NAMES,
	PRODUCTS_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-products-settings';
import {
	AsyncGlobalProjectsSettingsSidebar,
	PROJECTS_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-projects-settings';
import {
	AsyncGlobalSystemSettingsSidebar,
	SYSTEM_SETTINGS_ROUTE_NAMES,
	useGlobalSystemSettingsConnectItemActive,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-system-settings';
import { AsyncJsmCmpProjectSettingsSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-jsm-cmp-project-settings';
import { AsyncJsmTmpProjectSettingsSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-jsm-tmp-project-settings';
import {
	AsyncPlanSettingsSidebar,
	PLAN_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-plan-settings';
import { Skeleton as ProjectSettingsSkeleton } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-common/src/common/ui/skeleton';
import { PROJECT_SETTINGS_ROUTE_NAMES } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-common/src/constants';
import { useIsConnectAppActive as useIsProjectSettingsConnectAppActive } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-common/src/controllers/app-items';
import { AsyncJpdProjectSettingsSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-jpd-settings';
import AsyncSidebarContent from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebar-content/src/async';
import AsyncSoftwareCompanyManagedProjectSettingsSidebar from '@atlassian/jira-navigation-apps-sidebar-nav4-software-company-managed-project-settings';
import AsyncSoftwareTeamManagedProjectSettingsSidebar from '@atlassian/jira-navigation-apps-sidebar-nav4-software-team-managed-project-settings';
import { LeftSidebar } from '@atlassian/jira-navigation-system';
import { PanelSplitter } from '@atlassian/jira-navigation-system/src/v4';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { useRouter } from '@atlassian/jira-router';
import { messages } from './messages';

const ProjectSettingsSidebarSelector = () => {
	const { data: project, loading, error } = useProjectContext();

	if (loading) {
		return (
			<SidebarContainer>
				<ProjectSettingsSkeleton />
			</SidebarContainer>
		);
	}

	if (project?.projectType === CORE_PROJECT) {
		return project.simplified ? (
			<AsyncBusinessTeamManagedProjectSettingsSidebar project={project} />
		) : (
			<AsyncBusinessCompanyManagedProjectSettingsSidebar project={project} />
		);
	}

	if (project?.projectType === SOFTWARE_PROJECT) {
		return project.simplified ? (
			<AsyncSoftwareTeamManagedProjectSettingsSidebar project={project} />
		) : (
			<AsyncSoftwareCompanyManagedProjectSettingsSidebar project={project} />
		);
	}

	if (project?.projectType === SERVICE_DESK_PROJECT) {
		return project.simplified ? (
			<AsyncJsmTmpProjectSettingsSidebar project={project} />
		) : (
			<AsyncJsmCmpProjectSettingsSidebar project={project} />
		);
	}

	if (project?.projectType === PRODUCT_DISCOVERY_PROJECT) {
		return <AsyncJpdProjectSettingsSidebar project={project} />;
	}

	if (error) {
		throw error;
	}

	throw new Error(
		`No project settings sidebar found for the project type: ${project?.projectType}`,
	);
};

const DefaultSidebar = () => {
	const { saveCurrentRoute } = useNavigateBack();

	// the route to navigate back to will be whichever route was last displayed
	// with the default sidebar on
	saveCurrentRoute();
	return <AsyncSidebarContent />;
};

const InternalSidebarSelector = () => {
	const [
		{
			match: { params },
			route,
			location,
		},
	] = useRouter();
	const {
		isActive: isGlobalAppsSettingsConnectItemActive,
		loading: globalAppsSettingsConnectItemsLoading,
	} = useGlobalAppsSettingsConnectItemActive();
	const {
		isActive: isGlobalSystemSettingsConnectItemActive,
		loading: globalSystemSettingsConnectItemsLoading,
	} = useGlobalSystemSettingsConnectItemActive();
	const {
		isActive: isProjectSettingsConnectAppActive,
		loading: projectSettingsConnectAppsLoading,
	} = useIsProjectSettingsConnectAppActive();

	// Project settings sidebars
	if (
		PROJECT_SETTINGS_ROUTE_NAMES.has(route.name) ||
		route.group === ROUTE_GROUPS.PROJECT_SETTINGS_SOFTWARE ||
		route.group === ROUTE_GROUPS.PROJECT_SETTINGS_CORE ||
		route.group === ROUTE_GROUPS.PROJECT_SETTINGS_SERVICEDESK ||
		(route.name === ROUTE_NAMES.POLARIS_IDEAS && params.section === 'settings') ||
		isProjectSettingsConnectAppActive
	) {
		return <ProjectSettingsSidebarSelector />;
	}

	// Global settings sidebars
	if (PROJECTS_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncGlobalProjectsSettingsSidebar />;
	}
	if (ISSUES_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncGlobalIssuesSettingsSidebar />;
	}
	// should not render AsyncGlobalAppsSettingsSidebar for the path '/jira/marketplace/discover' instead it should render DefaultSidebar
	if (
		(APPS_SETTINGS_ROUTE_NAMES.has(route.name) || isGlobalAppsSettingsConnectItemActive) &&
		!location.pathname.includes('/discover')
	) {
		return <AsyncGlobalAppsSettingsSidebar />;
	}
	if (PRODUCTS_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncGlobalProductsSettingsSidebar />;
	}
	if (
		PRODUCTS_SETTINGS_OPERATIONS_ROUTE_NAMES.has(route.name) &&
		!ff('platform.50cent.operations-general-configurations-sidebar-item_dqyta')
	) {
		return <AsyncGlobalProductsSettingsSidebar />;
	}

	if (SYSTEM_SETTINGS_ROUTE_NAMES.has(route.name) || isGlobalSystemSettingsConnectItemActive) {
		return <AsyncGlobalSystemSettingsSidebar />;
	}

	// Plan settings sidebars
	if (PLAN_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncPlanSettingsSidebar />;
	}

	/*
	 * Loading state placed towards the bottom of the component to allow for an early return
	 * as soon as an active connect item is found.
	 */
	if (
		globalAppsSettingsConnectItemsLoading ||
		globalSystemSettingsConnectItemsLoading ||
		projectSettingsConnectAppsLoading
	) {
		return (
			<SidebarContainer>
				<ProjectSettingsSkeleton />
			</SidebarContainer>
		);
	}

	return <DefaultSidebar />;
};

/**
 * This test ID is used in pollinator tests. Do not change lightly.
 * It's only added once the component is rendered client-side.
 */
const TEST_ID = 'page-layout.sidebar';

export const SidebarSelector = () => {
	const { formatMessage } = useIntl();

	return (
		<LeftSidebar testId={__SERVER__ ? undefined : TEST_ID}>
			<NavigateBackProvider>
				<InternalSidebarSelector />
				<PanelSplitter label={formatMessage(messages.sideBarPanelSplitterLabel)} />
			</NavigateBackProvider>
		</LeftSidebar>
	);
};
